import React, { useMemo, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { downloadCsv } from './csvExport';

const datePresets = [
  ['all', 'All history'], ['1', 'Last 1 day'], ['3', 'Last 3 days'],
  ['7', 'Last 7 days'], ['30', 'Last 30 days'], ['60', 'Last 60 days'],
  ['90', 'Last 90 days'], ['custom', 'Custom range'],
];
const pageSizes = [50, 100, 250, 500];
const fieldClass = 'rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100';
const buttonClass = 'rounded-md border border-zinc-300 px-3 py-2 text-sm text-zinc-900 disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-100';

const timestamp = (value) => {
  if (!value) return 0;
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  return new Date(value).getTime() || 0;
};

export const filterReportRows = (rows, settings, columns, filters, dateKey, now = Date.now()) => {
  const query = settings.search.trim().toLowerCase();
  const start = settings.dateRange === 'custom' && settings.startDate
    ? new Date(`${settings.startDate}T00:00:00`).getTime()
    : null;
  const end = settings.dateRange === 'custom' && settings.endDate
    ? new Date(`${settings.endDate}T23:59:59.999`).getTime()
    : null;
  const days = Number(settings.dateRange);
  return rows.filter((row) => {
    const date = timestamp(row[dateKey]);
    if (Number.isFinite(days) && days > 0 && date < now - days * 86400000) return false;
    if (start !== null && date < start) return false;
    if (end !== null && date > end) return false;
    if (query) {
      const searchable = [JSON.stringify(row, (key, value) => key.startsWith('_') ? undefined : value),
        ...columns.map((column) => column.value(row))].join(' ').toLowerCase();
      if (!searchable.includes(query)) return false;
    }
    return filters.every((filter) => {
      const selected = settings[filter.key];
      if (Array.isArray(selected)) return !selected.length || selected.includes(String(filter.value(row)));
      return !selected || selected === 'all' || String(filter.value(row)) === selected;
    });
  }).sort((a, b) => {
    const column = columns.find((item) => item.key === settings.sortKey) || columns[0];
    const left = column.sortValue ? column.sortValue(a) : column.value(a);
    const right = column.sortValue ? column.sortValue(b) : column.value(b);
    const normalizedLeft = left == null ? '' : left;
    const normalizedRight = right == null ? '' : right;
    const result = typeof normalizedLeft === 'number' && typeof normalizedRight === 'number'
      ? normalizedLeft - normalizedRight
      : String(normalizedLeft).localeCompare(String(normalizedRight), undefined, { numeric: true });
    return (settings.sortDir === 'asc' ? 1 : -1) * result || String(a.id).localeCompare(String(b.id));
  });
};

const defaultSettings = (columns, filters) => ({
    search: '', dateRange: 'all', startDate: '', endDate: '',
    sortKey: columns[0].key, sortDir: 'desc', pageSize: 100, page: 1,
    ...Object.fromEntries(filters.map((filter) => [filter.key, filter.multiple ? [] : 'all'])),
});
const initialSettings = (key, columns, filters) => {
  const defaults = defaultSettings(columns, filters);
  try {
    const stored = JSON.parse(window.localStorage.getItem(key) || '{}');
    const settings = {
      ...defaults, ...stored,
      sortKey: columns.some((column) => column.key === stored.sortKey) ? stored.sortKey : defaults.sortKey,
      pageSize: pageSizes.includes(stored.pageSize) ? stored.pageSize : defaults.pageSize,
    };
    filters.forEach((filter) => {
      const allowed = new Set(filter.options.map(([value]) => value));
      const selected = stored[filter.key];
      if (filter.multiple) {
        const values = Array.isArray(selected) ? selected :
          selected && selected !== 'all' ? [selected] : [];
        settings[filter.key] = values.filter((value) => allowed.has(value));
      } else if (selected !== 'all' && !allowed.has(selected)) {
        settings[filter.key] = 'all';
      }
    });
    return settings;
  } catch {
    return defaults;
  }
};

export default function ReportTable({ title, rows, loading, columns, filters = [], dateKey,
  storageKey, exportName, canExport, action, onRowClick }) {
  const defaults = useMemo(() => initialSettings(storageKey, columns, filters),
    [storageKey, columns, filters]);
  const [settings, setSettings] = useState(defaults);
  const update = (patch) => {
    const next = { ...settings, ...patch, page: patch.page || 1 };
    setSettings(next);
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  };
  const filtered = useMemo(() => filterReportRows(rows, settings, columns, filters, dateKey),
    [rows, settings, columns, filters, dateKey]);
  const pages = Math.max(1, Math.ceil(filtered.length / settings.pageSize));
  const page = Math.min(settings.page, pages);
  const visible = filtered.slice((page - 1) * settings.pageSize, page * settings.pageSize);
  const exportRows = (items, suffix) => {
    downloadCsv(items.map((row) => Object.fromEntries(columns.map((column) =>
      [column.label, column.value(row)]))),
      `${exportName}-${suffix}-${new Date().toISOString().slice(0, 10)}`,
      columns.map((column) => column.label));
  };
  const multiFilter = (filter) => {
    const selected = settings[filter.key];
    const selectedLabels = filter.options.filter(([value]) => selected.includes(value))
      .map(([, label]) => label);
    const summary = selectedLabels.length === 0 ? 'All stages' :
      selectedLabels.length <= 2 ? selectedLabels.join(', ') : `${selectedLabels.length} stages selected`;
    return <Popover className="relative text-sm" key={filter.key}>
      <span className="block">{filter.label}</span>
      <PopoverButton className={`${fieldClass} mt-1 flex w-full items-center justify-between gap-2 text-left`}
        aria-label={`${filter.label}: ${summary}`}>
        <span className="truncate">{summary}</span>
        <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0" />
      </PopoverButton>
      <PopoverPanel anchor="bottom start"
        className="z-30 w-64 rounded-md border border-zinc-200 bg-white p-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-800">
        <fieldset>
          <legend className="sr-only">{filter.label}</legend>
          {filter.options.map(([value, label]) => <label key={value}
            className="flex cursor-pointer items-center rounded px-2 py-1.5 text-sm text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-700">
            <input type="checkbox" className="mr-2 size-4 accent-zinc-900 dark:accent-zinc-100"
              checked={selected.includes(value)} onChange={() => update({
                [filter.key]: selected.includes(value)
                  ? selected.filter((item) => item !== value) : [...selected, value],
              })} />
            {label}
          </label>)}
        </fieldset>
        <button type="button" className="mt-2 px-2 py-1 text-sm underline disabled:opacity-40"
          disabled={!selected.length} onClick={() => update({ [filter.key]: [] })}>
          Clear selection
        </button>
      </PopoverPanel>
    </Popover>;
  };

  return <div className="mx-auto max-w-7xl text-zinc-900 dark:text-zinc-100">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="text-2xl font-semibold">{title}</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">{filtered.length} of {rows.length} records</p></div>
      <div className="flex flex-wrap gap-2">{action}
        {canExport && <><button type="button" className={buttonClass} disabled={!filtered.length}
          onClick={() => exportRows(filtered, 'filtered')}>Export filtered</button>
          <button type="button" className={buttonClass} disabled={!rows.length}
            onClick={() => exportRows(rows, 'all')}>Export all</button></>}</div>
    </div>
    <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <label className="text-sm">Search<input className={`${fieldClass} mt-1 w-full`} value={settings.search}
        onChange={(event) => update({ search: event.target.value })} placeholder="Search records" /></label>
      <label className="text-sm">Date range<select className={`${fieldClass} mt-1 w-full`}
        value={settings.dateRange} onChange={(event) => update({ dateRange: event.target.value })}>
        {datePresets.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      {filters.map((filter) => filter.multiple ? multiFilter(filter) :
        <label className="text-sm" key={filter.key}>{filter.label}
        <select className={`${fieldClass} mt-1 w-full`} value={settings[filter.key] || 'all'}
          onChange={(event) => update({ [filter.key]: event.target.value })}>
          <option value="all">All</option>{filter.options.map(([value, label]) =>
            <option key={value} value={value}>{label}</option>)}</select></label>)}
      {settings.dateRange === 'custom' && <><label className="text-sm">From
        <input type="date" className={`${fieldClass} mt-1 w-full`} value={settings.startDate}
          onChange={(event) => update({ startDate: event.target.value })} /></label>
        <label className="text-sm">Through<input type="date" className={`${fieldClass} mt-1 w-full`}
          value={settings.endDate} onChange={(event) => update({ endDate: event.target.value })} /></label></>}
      <label className="text-sm">Rows per page<select className={`${fieldClass} mt-1 w-full`}
        value={settings.pageSize} onChange={(event) => update({ pageSize: Number(event.target.value) })}>
        {pageSizes.map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
      <button type="button" className={`${buttonClass} self-end`}
        onClick={() => update(defaultSettings(columns, filters))}>Reset view</button>
    </div>
    <div className="mt-5 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
      <table className="min-w-full divide-y divide-zinc-200 bg-white dark:divide-zinc-700 dark:bg-zinc-900">
        <thead className="bg-zinc-100 dark:bg-zinc-800"><tr>{columns.map((column) =>
          <th key={column.key} scope="col" className="px-4 py-2 text-left text-xs uppercase"
            aria-sort={settings.sortKey === column.key
              ? (settings.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button type="button" onClick={() => update({ sortKey: column.key,
              sortDir: settings.sortKey === column.key && settings.sortDir === 'asc' ? 'desc' : 'asc' })}>
              {column.label}{settings.sortKey === column.key ? (settings.sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
            </button></th>)}</tr></thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">{visible.map((row) =>
          <tr key={row.id} className={onRowClick ? 'cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800' : ''}
            onClick={() => onRowClick?.(row)}>
            {columns.map((column) =>
              <td key={column.key} className="px-4 py-2 text-sm">{column.render ? column.render(row) : column.value(row)}</td>)}</tr>)}
          {!loading && !visible.length && <tr><td colSpan={columns.length} className="p-6 text-center text-sm">No matching records</td></tr>}
          {loading && <tr><td colSpan={columns.length} className="p-6 text-center text-sm">Loading...</td></tr>}</tbody>
      </table>
    </div>
    <div className="mt-4 flex items-center justify-end gap-2 text-sm">
      <span>Page {page} of {pages}</span>
      <button type="button" className={buttonClass} disabled={page <= 1} onClick={() => update({ page: 1 })}>First</button>
      <button type="button" className={buttonClass} disabled={page <= 1} onClick={() => update({ page: page - 1 })}>Previous</button>
      <button type="button" className={buttonClass} disabled={page >= pages} onClick={() => update({ page: page + 1 })}>Next</button>
      <button type="button" className={buttonClass} disabled={page >= pages} onClick={() => update({ page: pages })}>Last</button>
    </div>
  </div>;
}
